<script setup>
/**
 * The Agency Hub sidebar's icons, as the prototype's staff sidebar draws them
 * (SNAV, ic.* — 28 Oct build): one outline set, one stroke, the current
 * colour, so the selected item's purple is the icon's too.
 *
 * Each says what the item is. Sessions is a video camera — a session is a
 * call, and the calendar is only one way to look at the list; Recordings is a
 * film strip, so the two never share an idea. Messages is an envelope —
 * correspondence with a policyholder — because a speech bubble is in-session
 * chat.
 */
const props = defineProps({
  /** home · users · mail · megaphone · video · film · book · shield · door · chart · sliders */
  name: { type: String, required: true },
  size: { type: Number, default: 18 },
});

const SHAPES = {
  home: [['path', { d: 'M3.6 11.2 12 4.4l8.4 6.8' }], ['path', { d: 'M5.8 10.1V19a1.2 1.2 0 0 0 1.2 1.2h10a1.2 1.2 0 0 0 1.2-1.2v-8.9' }], ['path', { d: 'M10 20.2v-5.1h4v5.1' }]],
  users: [['circle', { cx: 9, cy: 8, r: 3.2 }], ['path', { d: 'M3.6 20v-1.4A4 4 0 0 1 7.6 14.6h2.8a4 4 0 0 1 4 4V20' }], ['circle', { cx: 17.4, cy: 9, r: 2.4 }], ['path', { d: 'M16 14.7h1.8a3 3 0 0 1 3 3V20' }]],
  mail: [['rect', { x: 3.5, y: 5.5, width: 17, height: 13, rx: 2.5 }], ['path', { d: 'm4.6 8 7.4 5 7.4-5' }]],
  megaphone: [['path', { d: 'M4.2 10.2v3.6a1.6 1.6 0 0 0 1.6 1.6h2.1l8.1 4.2V4.4L7.9 8.6H5.8a1.6 1.6 0 0 0-1.6 1.6z' }], ['path', { d: 'M19 9.4a4 4 0 0 1 0 5.2' }], ['path', { d: 'M8 15.4v3a1.6 1.6 0 0 0 3.2 0v-1.4' }]],
  video: [['rect', { x: 3.5, y: 6.5, width: 12, height: 11, rx: 2.5 }], ['path', { d: 'M15.5 11.2 20.5 8.6v6.8l-5-2.6z' }]],
  film: [['rect', { x: 3.6, y: 4.6, width: 16.8, height: 14.8, rx: 2.5 }], ['path', { d: 'M8 4.6v14.8M16 4.6v14.8' }], ['path', { d: 'M3.6 9.2H8M3.6 14.8H8M16 9.2h4.4M16 14.8h4.4' }]],
  book: [['path', { d: 'M12 6.6C10.4 5.2 8.4 4.6 5.4 4.6a1 1 0 0 0-1 1v11.6a1 1 0 0 0 1 1c3 0 5 .6 6.6 2 1.6-1.4 3.6-2 6.6-2a1 1 0 0 0 1-1V5.6a1 1 0 0 0-1-1c-3 0-5 .6-6.6 2z' }], ['path', { d: 'M12 6.6v13.6' }]],
  shield: [['path', { d: 'M12 4.2 5.6 6.7v4.8c0 3.9 2.6 7.2 6.4 8.6 3.8-1.4 6.4-4.7 6.4-8.6V6.7z' }], ['path', { d: 'm9.6 12 1.7 1.7 3.4-3.6' }]],
  door: [['rect', { x: 5.4, y: 3.8, width: 9.6, height: 16.4, rx: 1.4 }], ['path', { d: 'M3.6 20.2h16.8' }], ['circle', { cx: 12.4, cy: 12.2, r: 0.95 }]],
  chart: [['path', { d: 'M4.2 19.8h15.6' }], ['path', { d: 'M7.6 19.8v-6M12 19.8V7.2M16.4 19.8v-9' }]],
  sliders: [['path', { d: 'M4.4 7.4h11M18.6 7.4h1M4.4 16.6h1M8.6 16.6h11' }], ['circle', { cx: 17, cy: 7.4, r: 2.4 }], ['circle', { cx: 7, cy: 16.6, r: 2.4 }]],
};

// The prototype's optical compensation (J): smaller icons get a heavier line.
const strokeWidth = (size) => (size < 13 ? 2.1 : size < 17 ? 1.9 : 1.7);
</script>

<template>
  <svg
    :width="props.size"
    :height="props.size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    :stroke-width="strokeWidth(props.size)"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    :data-icon="props.name"
  >
    <component :is="tag" v-for="([tag, attrs], i) in SHAPES[props.name] ?? []" :key="i" v-bind="attrs" />
  </svg>
</template>
